// src/components/admin/AdminLayout.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import {
  LayoutDashboard,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  FlaskConical,
  Bot,
  Home
} from 'lucide-react';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

interface NavItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  submenu?: { label: string; path: string }[];
}

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const navItems: NavItem[] = [
    {
      icon: <LayoutDashboard size={20} />,
      label: 'Dashboard',
      path: '/admin'
    },
    {
      icon: <Users size={20} />,
      label: 'Users',
      path: '/admin/users'
    },
    {
      icon: <FlaskConical size={20} />,
      label: 'Courses',
      path: '/admin/courses'
    },
    {
      icon: <Settings size={20} />,
      label: 'Settings',
      path: '/admin/settings',
      submenu: [
        { label: 'General', path: '/admin/settings' },
        { label: 'AI Assistant', path: '/admin/settings/ai' },
        { label: 'Authentication', path: '/admin/settings/auth' }
      ]
    }
  ];

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const toggleSubmenu = (label: string) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <Link to="/courses" className="sidebar-logo">
            {!collapsed && <span>CHEM Virtual Lab</span>}
            <FlaskConical size={24} />
          </Link>
          <button
            className="collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <div key={item.path} className="nav-item-wrapper">
              {item.submenu ? (
                <>
                  <button
                    className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => toggleSubmenu(item.label)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {!collapsed && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        <ChevronRight
                          size={16}
                          className={`submenu-arrow ${expandedMenus[item.label] ? 'expanded' : ''}`}
                        />
                      </>
                    )}
                  </button>
                  {!collapsed && expandedMenus[item.label] && (
                    <div className="submenu">
                      {item.submenu.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className={`submenu-item ${location.pathname === sub.path ? 'active' : ''}`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {!collapsed && <span className="nav-label">{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Link to="/courses" className="nav-item">
            <span className="nav-icon"><Home size={20} /></span>
            {!collapsed && <span className="nav-label">Back to App</span>}
          </Link>
          <button className="nav-item logout" onClick={handleLogout}>
            <span className="nav-icon"><LogOut size={20} /></span>
            {!collapsed && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <Outlet />
      </main>

      <style>{`
        .admin-layout {
          display: flex;
          min-height: 100vh;
          background: #0f0f0f;
        }

        .admin-sidebar {
          width: ${SIDEBAR_WIDTH}px;
          background: #1a1a1a;
          border-right: 1px solid rgba(255,255,255,0.08);
          display: flex;
          flex-direction: column;
          transition: width 0.2s ease;
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 100;
        }

        .admin-sidebar.collapsed {
          width: ${SIDEBAR_COLLAPSED_WIDTH}px;
        }

        .sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #a5b4fc;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
        }

        .collapse-btn {
          background: rgba(255,255,255,0.05);
          border: none;
          color: #888;
          padding: 6px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .collapse-btn:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
        }

        .sidebar-nav {
          flex: 1;
          padding: 12px 8px;
          overflow-y: auto;
        }

        .nav-item-wrapper {
          margin-bottom: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          color: #888;
          text-decoration: none;
          border-radius: 8px;
          cursor: pointer;
          background: transparent;
          border: none;
          width: 100%;
          text-align: left;
          font-size: 14px;
          transition: all 0.15s;
        }

        .nav-item:hover {
          background: rgba(255,255,255,0.05);
          color: #fff;
        }

        .nav-item.active {
          background: rgba(99, 102, 241, 0.15);
          color: #a5b4fc;
        }

        .nav-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
        }

        .nav-label {
          flex: 1;
          white-space: nowrap;
        }

        .submenu-arrow {
          transition: transform 0.2s;
        }

        .submenu-arrow.expanded {
          transform: rotate(90deg);
        }

        .submenu {
          margin-left: 32px;
          padding: 4px 0;
        }

        .submenu-item {
          display: block;
          padding: 8px 12px;
          color: #666;
          text-decoration: none;
          font-size: 13px;
          border-radius: 6px;
          transition: all 0.15s;
        }

        .submenu-item:hover {
          color: #aaa;
          background: rgba(255,255,255,0.03);
        }

        .submenu-item.active {
          color: #a5b4fc;
          background: rgba(99, 102, 241, 0.1);
        }

        .sidebar-footer {
          padding: 12px 8px;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .nav-item.logout:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #f87171;
        }

        .admin-main {
          flex: 1;
          margin-left: ${SIDEBAR_WIDTH}px;
          transition: margin-left 0.2s ease;
          min-height: 100vh;
        }

        .admin-sidebar.collapsed + .admin-main {
          margin-left: ${SIDEBAR_COLLAPSED_WIDTH}px;
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;
